import React, { useState, useEffect, useCallback,  useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Collapse,
  styled,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as CircleIcon,
  PlayArrow as PlayArrowIcon,
  Movie as MovieIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";

// Components
import MeGenBreadcrumbs, { BreadcrumbItem } from "./common/MeGenBreadcrumbs";
import ModMeGenAiUsage from "./common/ModMeGenAiUsage";
import AiUsageButton, { FetchAiUsage } from "./common/AiUsageButton";
import CardMeGenFinalVideoScript from "./CardMeGenFinalVideoScript";
import useRunPolling, { PollingEvent } from "./useRunPolling";
import { hydrateVoicePrompt, synchronizeVoicePrompt } from "./common/voicePrompt";

// Types
import {
  StoryPartItem,
  FinalScriptData,
  Story2SceneGraphState,
  StoryInput,
  FullPartsInput,
  STEPS_STORY,
  STEPS_VDO,
} from "./types/MeGenTypes";
import { Event, getStaticUrl, getStoryStaticUrl } from "./MeGenInterface";
import moment from "moment";

// Props
export type CardGenerateScriptProps = {
  projectId: number;
  projectName: string;
  seriesId?: number;
  seriesName?: string;
  threadId: string | null;
  canViewAiUsage?: boolean;
  onFetchAiUsage?: FetchAiUsage;
  aiUsageUserNameMap?: Record<string, string>;
  isRunning?: boolean;
  onNavigateBack?: () => void;
  onCreateRun?: (projectId: number, seriesId: number) => Promise<string | null>;
  onSubmitStory?: (threadId: string, story: StoryInput) => void;
  onSubmitParts?: (threadId: string, parts: FullPartsInput) => void;
  onGenerateVideo?: (threadId: string, script: FinalScriptData, dryRun: boolean) => void;
  onGeneratingChange?: (isGenerating: boolean) => void;
  onUploadYoutube?: (threadId: string, credentialId: number | null) => void;
  onEvent?: (e: Event) => void;
};

type ScriptMode = "input" | "review" | "generating";

// ── Styled Components ──────────────────────────────────────────────────────────
const Container = styled(Box)({ padding: "0 10px" });

const SectionCard = styled(Box)({
  backgroundColor: "white",
  borderRadius: "0.75rem",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  padding: "1.5rem",
  marginBottom: "1.5rem",
});

const SectionTitle = styled(Typography)({
  fontSize: "1.125rem",
  fontWeight: 700,
  color: "#1e293b",
  marginBottom: "1rem",
});

const FormLabel = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#64748b",
  marginBottom: "0.5rem",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "0.5rem",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: "1px" },
  },
  "& .MuiInputBase-input": {
    padding: "12px 14px",
    fontSize: "0.875rem",
    color: "#334155",
  },
});

const ActionButton = styled(Button)({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  padding: "10px 24px",
  borderRadius: "9999px",
});

const PrimaryButton = styled(ActionButton)({
  backgroundColor: "#f382a0",
  color: "white",
  "&:hover": { backgroundColor: "#c75578" },
  "&:disabled": { backgroundColor: "#94a3b8" },
});

const SuccessButton = styled(ActionButton)({
  backgroundColor: "#22c55e",
  color: "white",
  "&:hover": { backgroundColor: "#16a34a" },
  "&:disabled": { backgroundColor: "#94a3b8" },
});

const DangerButton = styled(ActionButton)({
  backgroundColor: "#ef4444",
  color: "white",
  "&:hover": { backgroundColor: "#dc2626" },
});

const SceneCard = styled(Box)({
  backgroundColor: "white",
  borderRadius: "0.75rem",
  border: "1px solid #e2e8f0",
  padding: "1rem",
  marginBottom: "1rem",
});

const SceneHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "0.75rem",
  padding: "0.5rem 0.75rem",
  backgroundColor: "#f8fafc",
  borderRadius: "0.5rem",
  borderBottom: "1px solid #e2e8f0",
});

const StepIndicator = styled(Box)<{ status: "done" | "active" | "pending" | "error" }>(
  ({ status }) => ({
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px solid",
    borderColor:
      status === "done" ? "#22c55e"
      : status === "active" ? "#3b82f6"
      : status === "error" ? "#ef4444"
      : "#e2e8f0",
    backgroundColor:
      status === "done" ? "#dcfce7"
      : status === "active" ? "#dbeafe"
      : status === "error" ? "#fee2e2"
      : "#f8fafc",
    color:
      status === "done" ? "#22c55e"
      : status === "active" ? "#3b82f6"
      : status === "error" ? "#ef4444"
      : "#94a3b8",
    transition: "all 0.2s ease",
  })
);

const StepRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: "1rem",
  padding: "0.75rem",
  backgroundColor: "#f8fafc",
  borderRadius: "0.5rem",
  marginBottom: "0.5rem",
  border: "1px solid #f1f5f9",
});

const StepLabel = styled(Typography)<{ status: "done" | "active" | "pending" | "error" }>(
  ({ status }) => ({
    flex: 1,
    fontSize: "0.875rem",
    fontWeight: status === "active" ? 600 : 500,
    color:
      status === "done" ? "#1e293b"
      : status === "active" ? "#3b82f6"
      : status === "error" ? "#ef4444"
      : "#94a3b8",
  })
);

const UploadBanner = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "1rem 1.5rem",
  backgroundColor: "#fef3c7",
  borderRadius: "0.75rem",
  marginBottom: "1.5rem",
  border: "1px solid #fcd34d",
});

// ── Component ──────────────────────────────────────────────────────────────────
const CardGenerateScript: React.FC<CardGenerateScriptProps> = (props) => {
  const isRunning = props.isRunning ?? false;

  // ── State ────────────────────────────────────────────────────────────────────
  const [mode, setMode] = useState<ScriptMode>("input");
  const [isProgressExpanded, setIsProgressExpanded] = useState(true);
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [parts, setParts] = useState<StoryPartItem[]>([]);
  const [scriptWithImage, setScriptWithImage] = useState<FinalScriptData | null>(null);

  const [loadingStory, setLoadingStory] = useState(false);
  const [loadingParts, setLoadingParts] = useState(false);
  const [loadingVideo, setLoadingVideo] = useState(false);

  const [graphState, setGraphState] = useState<Story2SceneGraphState | undefined>(undefined);

  // ── Polling ──────────────────────────────────────────────────────────────────
  const handlePollingSuccess = useCallback(
    (newState: Story2SceneGraphState, isGenerating: boolean) => {
      setGraphState(prev => {
        if (prev && JSON.stringify(prev) === JSON.stringify(newState)) return prev;
        return newState;
      });
      props.onGeneratingChange?.(isGenerating);
    },
    [props.onGeneratingChange]
  );

  useRunPolling({
    threadId: props.threadId,
    isGenerating: isRunning,
    eventMessage: "HandleGetStory2SceneState",
    onEvent: props.onEvent! as (e: PollingEvent) => void,
    onSuccess: handlePollingSuccess,
  });

  // ── Sync graphState → local state ───────────────────────────────────────────
  useEffect(() => {
    if (!graphState?.values) return;
    const values = graphState.values;

    if (values.story) {
      setTitle(values.story.title || "");
      setDescription(values.story.description || "");
    }
    if (values.full_parts?.items) {
      setParts(values.full_parts.items);
    }
    if (values.final_script) {
      setScriptWithImage({
        ...values.final_script,
        items: values.final_script.items.map(hydrateVoicePrompt),
      });
    }

    // ── Sync mode ──
    const progress = graphState.progress || {};
    const isVdoRunning = STEPS_VDO.some(s => progress[s.id]?.status === "start");
    const hasFinalScript = (values.final_script?.items?.length || 0) > 0;
    const hasFinalVideo = !!values.final_video_path;

    if (isRunning && (isVdoRunning || hasFinalVideo)) {
      setMode("generating");
    } else if (hasFinalScript || hasFinalVideo) {
      setMode("review");
    }
    // else: keep current mode (ไม่ reset กลับ input เองถ้า user อยู่ที่ input)
  }, [graphState, isRunning]);

  // ── Sync loading states ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!graphState?.progress) return;
    const p = graphState.progress;

    if (p.s2s_ai_split_parts?.status === "done") setLoadingStory(false);
    if (p.s2s_save_result_to_json?.status === "done") setLoadingParts(false);

    const vdoFinalSteps = ["create_thumbnail", "upload_to_youtube"];
    const hasVdoStep = vdoFinalSteps.some(s => p[s]);
    const hasError = Object.values(p).some(v => v.status === "error");
    if (hasVdoStep || hasError) setLoadingVideo(false);
  }, [graphState?.progress]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const [isAiUsageOpen, setIsAiUsageOpen] = useState(false);

  const runName = useMemo(() => {
    const scriptTitle = graphState?.values?.final_script?.title_podcast;
    const genTitle = props.threadId ? `Story ${props.threadId.slice(0, 8).toUpperCase()}` : "Story";

    return scriptTitle || genTitle;
  }, [graphState?.values, props.threadId]);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: props.projectName, path: `/project/${props.projectId}` },
    ...(props.seriesName ? [{ label: props.seriesName, path: `/series/${props.seriesId}` }] : []),
    { label: runName },
  ];

  const getStepStatus = (stepId: string): "done" | "active" | "pending" | "error" => {
    const s = graphState?.progress?.[stepId]?.status;
    if (s === "done") return "done";
    if (s === "start") return "active";
    if (s === "error") return "error";
    return "pending";
  };

  const isReadyForUpload = graphState?.next?.includes("upload_to_youtube");

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleSubmitStory = async () => {
    if (!title.trim() || !description.trim()) return;
    setLoadingStory(true);
    setParts([]);
    setScriptWithImage(null);

    try {
      let currentThreadId = props.threadId;
      if (!currentThreadId && props.onCreateRun && props.seriesId) {
        currentThreadId = await props.onCreateRun(props.projectId, props.seriesId);
      }
      if (currentThreadId && props.onSubmitStory) {
        props.onSubmitStory(currentThreadId, { title, description });
      }
    } catch {
      setLoadingStory(false);
    }
  };

  const handleSubmitParts = () => {
    if (!props.threadId || parts.length === 0) return;
    const validParts = parts.filter(p => p.title?.trim() && p.description?.trim());
    if (validParts.length === 0) return;

    try {
      setLoadingParts(true);
      setScriptWithImage(null);
      props.onSubmitParts?.(props.threadId, { title, items: validParts });
    } catch {
      setLoadingParts(false);
    }
  };

  const handleGenerateVideo = () => {
    if (!props.threadId || !scriptWithImage) return;
    const validScript = {
      ...scriptWithImage,
      items: scriptWithImage.items
        .map(synchronizeVoicePrompt)
        .filter(item => item.voice_script.trim()),
    };
    setLoadingVideo(true);
    setMode("generating");
    props.onGenerateVideo?.(props.threadId, validScript, true);
  };

  const updatePart = (index: number, field: keyof StoryPartItem, value: string) => {
    setParts(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const updateScriptItem = (index: number, field: string, value: string) => {
    if (!scriptWithImage) return;
    setScriptWithImage(prev => {
      if (!prev) return prev;
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const handleUploadYoutube = () => {
    if (!props.threadId) return;
    if (!window.confirm("Are you sure you want to upload this video to YouTube?")) return;
    props.onUploadYoutube?.(props.threadId, null); // *****
  };

  const handleBackToInput = () => {
    setMode("input");
  };

  // ── Active steps based on mode ───────────────────────────────────────────────
  const getActiveSteps = () => {
    if (mode === "input") return [...STEPS_STORY];
    return [...STEPS_STORY, ...STEPS_VDO];
  };

  // ── URL Helper ───────────────────────────────────────────────────────────────
  const mediaVersion = moment(graphState?.created_at).format("DDMMYYYY_HHmmss") || "0";

  const buildUrl = useCallback(
    (path: string) => {
      const base = getStoryStaticUrl(path);
      return base ? `${base}?v=${mediaVersion}` : "";
    },
    [props.threadId, mediaVersion]
  );

  const buildOutputUrl = useCallback(
    (path: string | null | undefined) => {
      const base = getStaticUrl(path as string);

      return base ? `${base}?v=${mediaVersion}` : "";
    },
    [mediaVersion]
  );

  // ── Render: Progress Steps ───────────────────────────────────────────────────
  const renderProgressSteps = () => {
    if (!graphState?.progress || Object.keys(graphState.progress).length === 0) return null;

    const steps = getActiveSteps();

    return (
      <SectionCard id="megen-gen-script-progress-card">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: isProgressExpanded ? 2 : 0,
            pb: isProgressExpanded ? 1 : 0,
            borderBottom: isProgressExpanded ? "1px solid #e2e8f0" : "none",
          }}
        >
          <SectionTitle sx={{ mb: 0 }}>
            Progress Steps {isRunning && "(Running...)"}
          </SectionTitle>
          <IconButton
            id="megen-gen-script-progress-expand-btn"
            onClick={() => setIsProgressExpanded(p => !p)}
            size="small"
            sx={{ color: "#64748b", "&:hover": { backgroundColor: "#f1f5f9" } }}
          >
            {isProgressExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse id="megen-gen-script-progress-collapse" in={isProgressExpanded}>
          <Box>
            {steps.map(step => {
              const status = getStepStatus(step.id);
              const progressData = graphState?.progress?.[step.id];
              const stepDuration = graphState?.values?.step_durations?.[step.id];
              const errorMsg = status === "error"
                ? progressData?.detail || "An error occurred"
                : null;

              // auto-expand active step หรือ manually toggled
              const isExpanded = status === "active" || expandedStepId === step.id;

              return (
                <Box key={step.id}>
                  {/* ===== Step Row (Clickable) ===== */}
                  <StepRow
                    id={`megen-gen-script-step-${step.id}`}
                    onClick={() => setExpandedStepId(prev => prev === step.id ? null : step.id)}
                    sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#f1f5f9" } }}
                  >
                    {/* Indicator */}
                    <Box sx={{ pt: "2px", flexShrink: 0 }}>
                      <StepIndicator status={status}>
                        {status === "done" && <CheckCircleIcon sx={{ fontSize: 16 }} />}
                        {status === "active" && <CircularProgress size={14} sx={{ color: "#3b82f6" }} />}
                        {status === "error" && <ErrorIcon sx={{ fontSize: 16 }} />}
                        {status === "pending" && <CircleIcon sx={{ fontSize: 16 }} />}
                      </StepIndicator>
                    </Box>

                    {/* Label + Badges */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <StepLabel status={status}>{step.label}</StepLabel>

                        {/* detail inline (active only) */}
                        {status === "active" && progressData?.detail && (
                          <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontStyle: "italic", mr: 1 }}>
                            — {progressData.detail}
                          </Typography>
                        )}

                        {status === "done" && stepDuration && (
                          <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontFamily: "monospace", flexShrink: 0, ml: 1 }}>
                            {Number(stepDuration).toFixed(2)}s
                          </Typography>
                        )}
                        {status === "done" && (
                          <Box sx={{ ml: 1, fontSize: "0.7rem", fontWeight: 700, px: 1, py: 0.25, borderRadius: "9999px", backgroundColor: "#dcfce7", color: "#16a34a", flexShrink: 0 }}>
                            Success
                          </Box>
                        )}
                        {status === "active" && (
                          <Typography sx={{ fontSize: "0.75rem", color: "#3b82f6", fontWeight: 600, flexShrink: 0, ml: 1 }}>
                            Running...
                          </Typography>
                        )}
                        {status === "error" && (
                          <Box sx={{ ml: 1, fontSize: "0.7rem", fontWeight: 700, px: 1, py: 0.25, borderRadius: "9999px", backgroundColor: "#fee2e2", color: "#dc2626", flexShrink: 0 }}>
                            Error
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </StepRow>

                  {/* ===== Collapsible Detail Panel ===== */}
                  <Collapse in={isExpanded && !!progressData}>
                    <Box
                      sx={{
                        ml: "3rem",
                        mt: "-0.25rem",
                        mb: "0.75rem",
                        p: "0.75rem",
                        backgroundColor: "#f8fafc",
                        borderRadius: "0.5rem",
                        border: "1px solid #f1f5f9",
                      }}
                    >
                      {/* Error detail */}
                      {errorMsg && (
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            color: "#dc2626",
                            backgroundColor: "#fff5f5",
                            border: "1px solid #fecaca",
                            borderRadius: "0.375rem",
                            px: 1.5,
                            py: 0.75,
                            wordBreak: "break-word",
                            mb: 1,
                          }}
                        >
                          {errorMsg}
                        </Typography>
                      )}

                      {/* Progress bar */}
                      {progressData && (progressData.total || 0) > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>Processing items...</Typography>
                            <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                              {progressData.current} / {progressData.total}
                            </Typography>
                          </Box>
                          <Box sx={{ height: 6, borderRadius: 3, backgroundColor: "#e2e8f0", overflow: "hidden" }}>
                            <Box
                              sx={{
                                height: "100%",
                                width: `${((progressData.current || 0) / (progressData.total || 0)) * 100}%`,
                                backgroundColor: status === "done" ? "#22c55e" : status === "error" ? "#ef4444" : "#3b82f6",
                                borderRadius: 3,
                                transition: "width 0.3s ease",
                              }}
                            />
                          </Box>
                        </Box>
                      )}

                      {/* Current item */}
                      {progressData?.current_item && status === "active" && (
                        <Typography sx={{ fontSize: "0.75rem", color: "#64748b", mb: 0.5 }}>
                          Current: <strong>{progressData.current_item}</strong>
                        </Typography>
                      )}

                      {/* Logs */}
                      {progressData?.logs && progressData.logs.length > 0 && (
                        // column-reverse + reverse array: ลำดับที่เห็นยังเป็น เก่า→ใหม่ แต่ scroll ปักที่บรรทัดล่าสุดขณะ log ไหลเข้า
                        <Box sx={{ mt: 1, maxHeight: "150px", overflowY: "auto", display: "flex", flexDirection: "column-reverse" }}>
                          {[...progressData.logs].reverse().map((log: string, i: number, arr: string[]) => (
                            <Typography
                              key={arr.length - 1 - i}
                              sx={{ fontSize: "0.75rem", fontFamily: "monospace", color: "#64748b", lineHeight: 1.5 }}
                            >
                              {log}
                            </Typography>
                          ))}
                        </Box>
                      )}

                      {/* Empty state */}
                      {progressData && !progressData.logs?.length && !progressData.current_item && !((progressData.total || 0) > 0) && !errorMsg && (
                        <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontStyle: "italic" }}>
                          No detail available
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </SectionCard>
    );
  };

  // ── Render: Input Mode (Story + Parts) ──────────────────────────────────────
  const renderInputMode = () => (
    <>
      {/* Story Input */}
      <SectionCard id="megen-gen-script-story-card">
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <SectionTitle sx={{ mb: 0 }}>Story</SectionTitle>
          {scriptWithImage?.items && scriptWithImage.items.length > 0 && (
            <Button
              id="megen-gen-script-goto-review-btn"
              variant="text"
              onClick={() => setMode("review")}
              sx={{ textTransform: "none", color: "#3b82f6" }}
            >
              ไปที่ Review Script →
            </Button>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormLabel>Title</FormLabel>
          <StyledTextField
            id="megen-gen-script-title-input"
            fullWidth
            placeholder="หัวข้อเรื่อง"
            value={title}
            onChange={e => setTitle(e.target.value)}
            disabled={loadingStory}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <FormLabel>Story</FormLabel>
          <StyledTextField
            id="megen-gen-script-story-input"
            fullWidth
            multiline
            rows={6}
            placeholder="พิมพ์เรื่องราวที่นี่..."
            value={description}
            onChange={e => setDescription(e.target.value)}
            disabled={loadingStory}
          />
        </Box>
        <PrimaryButton
          id="megen-gen-script-submit-story-btn"
          startIcon={
            loadingStory
              ? <CircularProgress size={16} sx={{ color: "white" }} />
              : <PlayArrowIcon />
          }
          onClick={handleSubmitStory}
          disabled={loadingStory || loadingParts || loadingVideo || !title.trim() || !description.trim()}
        >
          {loadingStory ? "กำลังแตก Part..." : "ให้ AI แตก Part"}
        </PrimaryButton>
      </SectionCard>

      {/* Parts Editor */}
      {parts.length > 0 && (
        <SectionCard id="megen-gen-script-parts-card">
          <SectionTitle>แก้ไข Scene: {title}</SectionTitle>

          {parts.map((part, index) => (
            <SceneCard id={`megen-gen-script-part-${index + 1}`} key={index}>
              <SceneHeader>
                <Typography sx={{ fontWeight: 600, color: "#1e293b" }}>
                  Scene {index + 1}
                </Typography>
              </SceneHeader>
              <Box sx={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 2, mb: 2 }}>
                <Box>
                  <FormLabel>Title</FormLabel>
                  <StyledTextField
                    fullWidth
                    value={part.title}
                    onChange={e => updatePart(index, "title", e.target.value)}
                    disabled={loadingParts}
                  />
                </Box>
                <Box>
                  <FormLabel>Mood</FormLabel>
                  <StyledTextField
                    fullWidth
                    value={part.mood}
                    onChange={e => updatePart(index, "mood", e.target.value)}
                    disabled={loadingParts}
                  />
                </Box>
              </Box>
              <Box>
                <FormLabel>Description</FormLabel>
                <StyledTextField
                  fullWidth
                  multiline
                  rows={3}
                  value={part.description}
                  onChange={e => updatePart(index, "description", e.target.value)}
                  disabled={loadingParts}
                />
              </Box>
            </SceneCard>
          ))}

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <SuccessButton
              id="megen-gen-script-submit-parts-btn"
              onClick={handleSubmitParts}
              disabled={loadingStory || loadingParts || loadingVideo}
            >
              {loadingParts ? "กำลังสร้าง Script..." : "ยืนยัน Parts ที่แก้ไขแล้ว"}
            </SuccessButton>
          </Box>
        </SectionCard>
      )}
    </>
  );

  const renderReviewMode = () => {
    if (!scriptWithImage?.items) return null;

    return (
      <SectionCard id="megen-gen-script-review-card">
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <SectionTitle sx={{ mb: 0 }}>Review Script: {title}</SectionTitle>
          <Button id="megen-gen-script-back-to-input-btn" variant="text" onClick={handleBackToInput} sx={{ textTransform: "none", color: "#64748b" }}>
            ← กลับไปแก้ไข Parts
          </Button>
        </Box>

        {scriptWithImage.items.map((item, index) => {
          const valueItem = graphState?.values?.items?.find(v => v.id === item.id);
          const audioUrl = valueItem?.audio_path ? buildUrl(valueItem.audio_path) : null;
          const imageUrl = valueItem?.image_path ? buildUrl(valueItem.image_path) : null;

          return (
            <SceneCard id={`megen-gen-script-review-scene-${item.id}`} key={item.id}>
              <SceneHeader>
                <Typography sx={{ fontWeight: 600, color: "#1e293b" }}>
                  Scene {index + 1} • {item.voice_name}
                </Typography>
              </SceneHeader>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                {/* ── Left: Script + Audio ── */}
                <Box>
                  {item.headline && (
                    <Box sx={{ mb: 2 }}>
                      <FormLabel>Headline</FormLabel>
                      <StyledTextField
                        fullWidth
                        value={item.headline}
                        onChange={e => updateScriptItem(index, "headline", e.target.value)}
                      />
                    </Box>
                  )}
                  <FormLabel>Voice Guide</FormLabel>
                  <StyledTextField
                    fullWidth
                    multiline
                    rows={4}
                    value={item.voice_guide ?? ""}
                    onChange={e => updateScriptItem(index, "voice_guide", e.target.value)}
                    placeholder="Style: ...\nTone: ..."
                  />
                  <FormLabel sx={{ mt: 1 }}>Voice Script</FormLabel>
                  <StyledTextField
                    fullWidth
                    multiline
                    rows={6}
                    value={item.voice_script ?? ""}
                    onChange={e => updateScriptItem(index, "voice_script", e.target.value)}
                  />
                  {audioUrl && (
                    <Box sx={{ mt: 1 }}>
                      <FormLabel sx={{ color: "#db2777", fontWeight: 700 }}>Generated Audio</FormLabel>
                      <Box
                        component="audio"
                        controls
                        src={audioUrl}
                        key={`${item.id}-audio-${mediaVersion}`}
                        sx={{ width: "100%", height: 32 }}
                      />
                    </Box>
                  )}
                </Box>

                {/* ── Right: Image Prompt + Image Preview ── */}
                <Box>
                  <FormLabel>Image Prompt</FormLabel>
                  <StyledTextField
                    fullWidth
                    multiline
                    rows={6}
                    value={item.image_prompt || ""}
                    onChange={e => updateScriptItem(index, "image_prompt", e.target.value)}
                  />
                  {imageUrl && (
                    <Box sx={{ mt: 1 }}>
                      <FormLabel sx={{ color: "#db2777", fontWeight: 700 }}>Generated Image</FormLabel>
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={item.headline || item.id}
                        key={`${item.id}-image-${mediaVersion}`}
                        sx={{ width: "100%", borderRadius: "0.5rem", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </SceneCard>
          );
        })}

        {/* Generate Video Button Footer */}
        <Box sx={{
          bottom: 0,
          backgroundColor: "#fdf0fb", padding: "1rem",
          display: "flex", justifyContent: "flex-end",
          borderRadius: "0 0 0.75rem 0.75rem",
          margin: "-1.5rem", marginTop: "1rem",
        }}>
          <PrimaryButton
            id="megen-gen-script-generate-video-btn"
            startIcon={loadingVideo ? <CircularProgress size={16} sx={{ color: "white" }} /> : <MovieIcon />}
            onClick={handleGenerateVideo}
            disabled={loadingStory || loadingParts || loadingVideo}
          >
            {loadingVideo ? "กำลังสร้างวิดีโอ..." : "สร้างวิดีโอ"}
          </PrimaryButton>
        </Box>
      </SectionCard>
    );
  };

  // ── Root Render ──────────────────────────────────────────────────────────────
  return (
    <Container id="megen-gen-script-root">
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <MeGenBreadcrumbs items={breadcrumbItems} onNavigate={() => props.onNavigateBack?.()} />
        </Box>
        <AiUsageButton
          id="megen-gen-script-ai-usage-btn"
          threadId={props.threadId}
          canViewAiUsage={props.canViewAiUsage}
          onFetchAiUsage={props.onFetchAiUsage}
          onOpen={() => setIsAiUsageOpen(true)}
        />
      </Box>

      {/* Progress Steps (แสดงเสมอถ้ามี progress) */}
      {renderProgressSteps()}

      {/* Final Video — แสดงก่อน content เหมือน DirectScript */}
      {!isRunning && (() => {
        const videoUrl = graphState?.values?.final_video_path
          ? buildUrl(graphState.values.final_video_path) ?? ""
          : "";
        return videoUrl ? (
          <CardMeGenFinalVideoScript videoUrl={videoUrl} onGenerateVideo={handleGenerateVideo} />
        ) : null;
      })()}

      {/* Content by mode */}
      {!isRunning && mode === "input" && renderInputMode()}
      {!isRunning && mode === "review" && renderReviewMode()}

      {/* generating mode: แสดงแค่ progress + assets ที่กำลัง gen */}
      {isRunning && (
        <SectionCard id="megen-gen-script-running-card">
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <PrimaryButton
              id="megen-gen-script-generate-video-running-btn"
              startIcon={<MovieIcon />}
              onClick={handleGenerateVideo}
              sx={{ backgroundColor: "#0ea5e9" }}
            >
              สร้างวิดีโอ
            </PrimaryButton>
          </Box>
        </SectionCard>
      )}
    
      {/* --- AI Usage (See more) --- */}
      <ModMeGenAiUsage
        open={isAiUsageOpen}
        threadId={props.threadId}
        userNameMap={props.aiUsageUserNameMap}
        threadTitle={runName}
        onClose={() => setIsAiUsageOpen(false)}
        onFetchUsage={props.onFetchAiUsage}
      />
    </Container>
  );
};

export default CardGenerateScript;
